import { RESET_STORE } from "../../../store/actionTypes";
import { fetchOrgCodes } from "../../../store/auth/getOrgScope/orgScopeSlice";

/**
 * Helper utility to switch organizations programmatically without showing the org-selection page
 * This provides a seamless user experience when switching from Margin Management to customer dashboards
 * 
 * @param {string} orgCode - The organization code to switch to
 * @param {object} options - Configuration options
 * @param {function} dispatch - Redux dispatch function
 * @param {function} history - React Router history object
 * @param {function} setHasSelectedOrganization - Function from OrgContext to update organization selection state
 * @param {array} orgCodes - Array of organization codes from Redux store (optional, will fetch if not provided)
 * @param {boolean} msp - MSP flag from Redux store (optional)
 * @param {string} mspOrgCode - MSP organization code from Redux store (optional)
 * @param {string} redirectPath - Optional redirect path (defaults to "/costinsight")
 * @returns {Promise<boolean>} - Returns true if successful, false otherwise
 */
export const switchToOrganization = async (
  orgCode,
  { 
    dispatch, 
    history, 
    setHasSelectedOrganization, 
    orgCodes: providedOrgCodes = null,
    msp: providedMsp = null,
    mspOrgCode: providedMspOrgCode = null,
    redirectPath = "/costinsight" 
  }
) => {
  try {
    console.log("🚀 ~ switchToOrganization ~ orgCode:", orgCode);

    // Step 1: Get orgCodes, msp, and mspOrgCode from parameters or localStorage/store
    let orgCodes = providedOrgCodes || [];
    let msp = providedMsp;
    let mspOrgCode = providedMspOrgCode || localStorage.getItem("mspOrgCode");

    // If orgCodes not provided, try to get from localStorage
    if (!orgCodes || orgCodes.length === 0) {
      const orgScopeDataStr = localStorage.getItem("orgScopeData");
      if (orgScopeDataStr) {
        try {
          orgCodes = JSON.parse(orgScopeDataStr);
        } catch (error) {
          console.error("Error parsing orgScopeData:", error);
        }
      }
    }

    // Step 2: Find the matching organization
    let matchingOrg = orgCodes.find((org) => org.orgCode === orgCode);

    // Step 3: If orgCodes not available or org not found, fetch from API
    if (!orgCodes.length || !matchingOrg) {
      console.log("🚀 ~ OrgCodes not available or org not found, fetching from API...");
      
      // Dispatch fetch action
      dispatch(fetchOrgCodes());
      
      // Wait for the API call to complete and check localStorage
      // Note: In production, you might want to use a more robust approach with Redux thunks
      await new Promise((resolve) => setTimeout(resolve, 800));
      
      // Check localStorage again after fetch
      const updatedOrgScopeDataStr = localStorage.getItem("orgScopeData");
      if (updatedOrgScopeDataStr) {
        try {
          orgCodes = JSON.parse(updatedOrgScopeDataStr);
          matchingOrg = orgCodes.find((org) => org.orgCode === orgCode);
        } catch (error) {
          console.error("Error parsing updated orgScopeData:", error);
        }
      }
    }

    // Step 4: If org still not found, return false
    if (!matchingOrg) {
      console.error("🚀 ~ Organization not found for orgCode:", orgCode);
      // Clear the flag when redirecting to org selection
      localStorage.removeItem("cameFromMarginManagement");
      // Fallback: redirect to org selection page if we can't find the org
      history.push("/orgSelection");
      return false;
    }

    // Step 5: Get msp flag - use provided value or infer from mspOrgCode
    if (msp === null || msp === undefined) {
      // If we're switching to the MSP organization, msp should be true
      if (matchingOrg.orgCode === mspOrgCode) {
        msp = true;
      } else {
        msp = !!mspOrgCode; // Infer from mspOrgCode existence
      }
    }

    // Step 6: Reset store before switching
    dispatch({ type: RESET_STORE });

    // Step 7: Perform all organization selection logic (same as handleOrganizationClick)
    console.log("🚀 ~ switchToOrganization ~ org:", matchingOrg);
    
    localStorage.setItem("selectedOrganization", JSON.stringify(matchingOrg));
    localStorage.setItem("hasSelectedOrganization", "true");
    
    if (msp && mspOrgCode) {
      localStorage.setItem("isMspOrg", "true");
    }
    
    // Set wafrOnly based on the selected organization
    localStorage.setItem("wafrOnly", matchingOrg.wafrOnly || "false");

    // Margin Management - UI visibility
    const marginManagementUIVisible = (matchingOrg.orgCode === mspOrgCode && msp) ? "true" : "false";
    localStorage.setItem("isMarginMangementUIVisible", marginManagementUIVisible);

    // Margin Management Kibana dashboard visibility
    const marginManagementDashboardVisible = (matchingOrg.orgCode !== mspOrgCode && msp) ? "true" : "false";
    localStorage.setItem("isMarginmanagementDashboardVisible", marginManagementDashboardVisible);

    // Customer Summary Dashboard visibility
    const customerSummaryVisible = (matchingOrg.orgCode === mspOrgCode && msp) ? "true" : "false";
    localStorage.setItem("isCustomerSummaryDashboardVisible", customerSummaryVisible);

    // Update OrgContext
    if (setHasSelectedOrganization) {
      setHasSelectedOrganization(true);
    }

    console.log("🚀 ~ switchToOrganization ~ wafrOnly:", matchingOrg.wafrOnly);

    // Step 8: Set flag to indicate we came from Margin Management (for back button)
    // Only set this flag when redirecting to /costinsight (not when going back to /customer-management)
    if (redirectPath === "/costinsight") {
      localStorage.setItem("cameFromMarginManagement", "true");
    } else if (redirectPath === "/customer-management") {
      // Clear the flag when going back to margin management
      localStorage.removeItem("cameFromMarginManagement");
    }

    // Step 9: Ensure all localStorage values are set before navigation
    // Force synchronous write to localStorage
    localStorage.setItem("isSwitchingOrganization", "true");
    
    // Step 10: Use a small delay to ensure localStorage is fully written before navigation
    // This prevents route guards from checking before values are set
    await new Promise((resolve) => setTimeout(resolve, 50));
    
    // Step 11: Redirect to the specified path (defaults to /costinsight)
    history.push(redirectPath);
    
    // Clear the flag after a short delay to allow component to mount
    // The component will detect the flag, wait, then make API calls
    setTimeout(() => {
      localStorage.removeItem("isSwitchingOrganization");
    }, 200);

    return true;
  } catch (error) {
    console.error("🚀 ~ Error in switchToOrganization:", error);
    // Clear the flag when redirecting to org selection on error
    localStorage.removeItem("cameFromMarginManagement");
    // On error, fallback to org selection page
    history.push("/orgSelection");
    return false;
  }
};

